/**
 * /fund — screens 3j (the ledger) and 3k (the contribute sheet).
 *
 * The app never touches money (D40). This screen generates payment links,
 * records a two-sided ledger — claim, then confirm (D45) — and reconciles
 * against the host's receipts (D48).
 *
 * Three rules are load-bearing and easy to break by accident:
 *   • There is no "hasn't paid" list, anywhere, ever (D51). Contributors only,
 *     and a count for everyone else.
 *   • The copyable handle, amount and QR are ALWAYS present, never conditional
 *     on the deep link working (D42) — the link format is community-reverse-
 *     engineered and has broken before.
 *   • On desktop the QR is the primary affordance and the deep link demotes to
 *     a text link (D44), because Venmo web does not pre-fill from parameters.
 *
 * The QR is generated in this file (D43). Never a third-party QR API: that would
 * leak the host's handle and the amount to a vendor and put a CDN in the middle
 * of a payment flow.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import type {
  Contribution, FundSummary, PayLink, PayMethod,
} from '@shared/types'
import { useStore } from '../lib/store'
import { ApiFailure, DEL, GET, PATCH, POST, writeOrQueue } from '../lib/api'
import {
  BigStepper, Body, Button, Card, Eyebrow, Field, Header, Notice,
  ProgressBar, Screen, Sheet, SkeletonCard, Spacer,
} from '../ui'

// ── formatting ──────────────────────────────────────────────────────────────

const HHMM = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', hour12: true })
const MONTH_DAY_LONG = new Intl.DateTimeFormat(undefined, { month: 'long', day: 'numeric' })

function bareTime(at: number): string {
  return HHMM.format(at).replace(/\s?[AP]\.?M\.?$/i, '')
}

/** "$540" / "$684.40" — whole dollars lose the cents, as the prototype does. */
function money(cents: number): string {
  const abs = Math.abs(cents)
  const body = abs % 100 === 0
    ? (abs / 100).toLocaleString()
    : (abs / 100).toFixed(2)
  return `${cents < 0 ? '−' : ''}$${body}`
}

function dayMs(dateStr: string | null): number | null {
  if (!dateStr) return null
  const [y, m, d] = dateStr.split('-').map((n) => parseInt(n, 10))
  if (!y || !m || !d) return null
  return new Date(y, m - 1, d, 12, 0, 0).getTime()
}

/** "Week 2" → "Week 1", so the carryover line names where the money came from. */
function previousWeekLabel(label: string): string {
  const m = /^(.*?)(\d+)\s*$/.exec(label.trim())
  if (!m) return 'last week'
  const n = parseInt(m[2], 10)
  return n > 1 ? `${m[1]}${n - 1}` : 'last week'
}

const METHOD_LABEL: Record<PayMethod, string> = {
  venmo: 'Venmo',
  cashapp: 'Cash App',
  paypal: 'PayPal',
  zelle: 'Zelle',
  cash: 'Cash',
  other: 'Other',
}

/** Methods with a URL format that actually exists (D42). */
const LINKABLE: PayMethod[] = ['venmo', 'cashapp', 'paypal']

type FundResponse = FundSummary & { surplusC?: number; shortfallC?: number; peopleIn?: number }

// ── desktop is a different layout, not a wider one (D44) ────────────────────

function useIsDesktop(): boolean {
  const [wide, setWide] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1025px)').matches,
  )
  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia('(min-width: 1025px)')
    const on = () => setWide(mq.matches)
    mq.addEventListener('change', on)
    return () => mq.removeEventListener('change', on)
  }, [])
  return wide
}

// ── screen ──────────────────────────────────────────────────────────────────

export default function Fund() {
  const { state, toast, refresh } = useStore()

  const weekId = state?.week?.id ?? state?.fund?.weekId ?? null

  const [fund, setFund] = useState<FundResponse | null>(state?.fund ?? null)
  const [err, setErr] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(state?.fund != null)
  const [chipOpen, setChipOpen] = useState(false)
  const [rowBusy, setRowBusy] = useState<string | null>(null)

  // `?edit=<id>` is how the timeline hands a chip-in over to this screen: the
  // Edit link there is an ordinary link to /fund, and the sheet opens on
  // arrival. Cleared from the URL as soon as it has been read, so a refresh or
  // a back button doesn't reopen a sheet the person already closed.
  const [params, setParams] = useSearchParams()
  const editParam = params.get('edit')
  const [editing, setEditing] = useState<Contribution | null>(null)
  const meId = state?.user.id ?? null

  const load = useCallback(async (signal?: AbortSignal) => {
    if (!weekId) { setLoaded(true); return }
    try {
      setFund(await GET<FundResponse>(`/api/fund/${weekId}`, signal))
      setErr(null)
    } catch (e) {
      if (signal?.aborted) return
      setErr(e instanceof ApiFailure ? e.message : 'Couldn’t refresh the fund.')
    } finally {
      if (!signal?.aborted) setLoaded(true)
    }
  }, [weekId])

  useEffect(() => {
    const ac = new AbortController()
    void load(ac.signal)
    return () => ac.abort()
  }, [load])

  useEffect(() => {
    if (!editParam || !fund) return
    const match = fund.contributions.find((c) => c.id === editParam)
    // Silently ignored when it isn't theirs or isn't there any more — an
    // arrival from a stale link should land on the fund screen, not on an
    // error about a row the person may not even remember.
    if (match && match.userId === meId) setEditing(match)
    const next = new URLSearchParams(params)
    next.delete('edit')
    setParams(next, { replace: true })
  }, [editParam, fund, meId, params, setParams])

  const hostAction = useCallback(async (id: string, action: 'confirm' | 'dispute') => {
    setRowBusy(id)
    try {
      await POST(`/api/fund/contributions/${id}/${action}`, action === 'dispute' ? {} : undefined)
      await load()
      void refresh(true)
    } catch (e) {
      setErr(e instanceof ApiFailure ? e.message : 'Couldn’t record that.')
    } finally {
      setRowBusy(null)
    }
  }, [load, refresh])

  const gatheringAt = dayMs(fund?.gatheringDate ?? null)
  const eyebrow = [
    fund?.weekLabel ?? state?.week?.label ?? 'This week',
    gatheringAt === null ? null : MONTH_DAY_LONG.format(gatheringAt),
  ].filter(Boolean).join(' · ')

  const targetC = fund?.targetC ?? 0
  const confirmedC = fund?.confirmedC ?? 0
  const pct = targetC > 0 ? (confirmedC / targetC) * 100 : 0

  return (
    <Screen style={{ flex: '1 1 auto', minHeight: 0 }}>
      <Header
        bordered
        left={<Eyebrow large>{eyebrow}</Eyebrow>}
        // No host line at all. Hosting is gone from the front end: it was an
        // open job listed on a screen whose purpose is paying, and it sat
        // there every week until somebody volunteered.

      />

      <Body style={{ padding: '18px var(--gutter) calc(24px + var(--nav-h))' }}>
        {!loaded ? (
          <>
            <SkeletonCard lines={3} />
            <Spacer h={12} />
            <SkeletonCard lines={4} />
          </>
        ) : !weekId || !fund ? (
          <Card style={{ padding: '26px 18px', textAlign: 'center' }}>
            <div style={{ fontSize: 15, fontWeight: 600 }}>No week to chip in for yet.</div>
            <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 6 }}>
              The season calendar hasn’t synced. Everything else still works.
            </div>
          </Card>
        ) : (
          <>
            {err ? <><Notice tone="warn">{err}</Notice><Spacer h={16} /></> : null}

            <div style={{ textAlign: 'center' }}>
              <div className="numerals" style={{ fontSize: 52 }}>{money(confirmedC)}</div>
              <ProgressBar pct={pct} height={10} style={{ margin: '14px 0 8px' }} />
              <div style={{ fontSize: 13, color: 'var(--dim)' }}>of {money(targetC)} target</div>
            </div>

            <div style={{ marginTop: 16, fontSize: 13, color: 'var(--dim)', lineHeight: 1.9 }}>
              {fund.pendingC > 0 ? <div>{money(fund.pendingC)} pending confirmation</div> : null}
              {fund.carryoverInC > 0 ? (
                <div>{money(fund.carryoverInC)} carried over from {previousWeekLabel(fund.weekLabel)}</div>
              ) : null}
              {fund.neededC > 0
                ? <div>{money(fund.neededC)} still needed</div>
                : <div>Target covered. Anything else rolls into next week.</div>}
            </div>

            <Spacer h={16} />
            <Button onClick={() => setChipOpen(true)}>Chip in</Button>

            <div
              style={{
                marginTop: 16, padding: '14px 0',
                borderTop: '1px solid var(--line)', borderBottom: '1px solid var(--line)',
                fontSize: 13, color: 'var(--dim)',
              }}
            >
              Suggested: {money(fund.suggestedC)}
              {typeof fund.peopleIn === 'number' ? ` · ${fund.peopleIn} people in` : ''}
              {fund.carryoverInC > 0 ? ` · ${money(fund.carryoverInC)} carried over` : ''}
            </div>

            {/* Contributors only. There is no "hasn't paid" list (D51). */}
            <Eyebrow style={{ marginTop: 20 }}>Contributions</Eyebrow>
            {fund.contributions.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 10 }}>
                Nobody has chipped in yet. Chipping in is always optional.
              </div>
            ) : (
              fund.contributions.map((c) => (
                <ContributionRow
                  key={c.id}
                  contribution={c}
                  canConfirm={fund.canConfirm}
                  canEdit={c.userId === meId}
                  busy={rowBusy === c.id}
                  onEdit={() => setEditing(c)}
                  onConfirm={() => void hostAction(c.id, 'confirm')}
                  onDispute={() => void hostAction(c.id, 'dispute')}
                />
              ))
            )}
            <div style={{ fontSize: 12, color: 'var(--dim)', marginTop: 10 }}>
              {fund.contributorCount} {fund.contributorCount === 1 ? 'person has' : 'people have'} chipped in
            </div>

            {/*
              Receipts are gone from this screen, and with them Spent and
              Variance. Those two numbers only ever came from receipts, and
              once there is no way to file one they are a permanent "$0" and a
              total that looks like an accounting error. What is left is the
              one number this screen exists for.

              The ledger behind them is untouched: `week_fund` still computes
              spend and variance, `/api/fund/:weekId/receipts` still accepts
              them, and the admin console still shows them.
            */}
            <div className="mono" style={{ fontSize: 12, marginTop: 12, lineHeight: 1.8 }}>
              Collected {money(fund.confirmedC)}
            </div>
          </>
        )}
      </Body>

      {fund && weekId ? (
        <ContributeSheet
          open={chipOpen}
          weekId={weekId}
          fund={fund}
          onClose={() => setChipOpen(false)}
          onDone={async (message) => {
            setChipOpen(false)
            if (message) toast(message)
            await load()
            void refresh(true)
          }}
        />
      ) : null}

      {editing ? (
        <EditContributionSheet
          contribution={editing}
          onClose={() => setEditing(null)}
          onDone={async (message) => {
            setEditing(null)
            if (message) toast(message)
            await load()
            void refresh(true)
          }}
        />
      ) : null}

    </Screen>
  )
}

// ── ledger rows ─────────────────────────────────────────────────────────────

function ContributionRow({
  contribution, canConfirm, canEdit, busy, onEdit, onConfirm, onDispute,
}: {
  contribution: Contribution
  canConfirm: boolean
  /** Yours to correct. The API enforces the same rule; this only hides the control. */
  canEdit: boolean
  busy: boolean
  onEdit: () => void
  onConfirm: () => void
  onDispute: () => void
}) {
  const confirmed = contribution.status === 'confirmed'
  const disputed = contribution.status === 'disputed'
  const showHostActions = canConfirm && !confirmed

  return (
    <div style={{ borderBottom: '1px solid var(--line)' }}>
      <div
        className="mono"
        style={{
          display: 'flex', justifyContent: 'space-between', gap: 12,
          fontSize: 12, padding: '10px 0',
          color: confirmed ? 'var(--fg)' : 'var(--dim)',
        }}
      >
        <Link
          to={`/u/${encodeURIComponent(contribution.userId)}`}
          style={{ minWidth: 0, color: 'inherit' }}
        >
          {contribution.displayName}
        </Link>
        <span style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span>
            {money(contribution.amountC)}
            {'  '}
            {confirmed ? (
              <>
                <span style={{ color: 'var(--bright)' }}>✓</span>
                {contribution.confirmedAt ? ` ${bareTime(contribution.confirmedAt)}` : ''}
              </>
            ) : disputed ? '⏳ needs a look' : '⏳ pending'}
          </span>
          {canEdit ? (
            /* Negative margin keeps a 44px target from stretching a 12px row. */
            <button
              onClick={onEdit}
              aria-label={`Edit your ${money(contribution.amountC)} chip-in`}
              style={{
                flex: '0 0 auto', margin: '-14px 0', padding: '14px 0 14px 4px', minHeight: 44,
                fontSize: 12, fontWeight: 600, color: 'var(--bright)',
              }}
            >
              Edit
            </button>
          ) : null}
        </span>
      </div>

      {showHostActions ? (
        <div style={{ display: 'flex', gap: 8, paddingBottom: 10 }}>
          <button
            onClick={onConfirm}
            disabled={busy}
            style={{
              flex: 1, padding: 10, borderRadius: 10, minHeight: 44, fontSize: 13, fontWeight: 600,
              background: 'var(--fill)', color: 'var(--ink)', opacity: busy ? 0.6 : 1,
            }}
          >
            {busy ? '…' : '✓ Received'}
          </button>
          <button
            onClick={onDispute}
            disabled={busy}
            style={{
              flex: 1, padding: 10, borderRadius: 10, minHeight: 44, fontSize: 13, fontWeight: 600,
              border: '1px solid var(--line)', color: 'var(--dim)', opacity: busy ? 0.6 : 1,
            }}
          >
            Didn’t get this
          </button>
        </div>
      ) : null}
    </div>
  )
}


// ── contribute sheet (3k) ───────────────────────────────────────────────────

interface LinkState {
  link: PayLink | null
  error: string | null
}

function ContributeSheet({
  open, weekId, fund, onClose, onDone,
}: {
  open: boolean
  weekId: string
  fund: FundResponse
  onClose: () => void
  onDone: (message?: string) => Promise<void>
}) {
  const desktop = useIsDesktop()

  // Venmo leads, then Cash App, then PayPal — the first one rendered is primary.
  const available = useMemo(
    () => fund.destinations
      .filter((d) => LINKABLE.includes(d.method) && d.confirmed)
      .sort((a, b) => LINKABLE.indexOf(a.method) - LINKABLE.indexOf(b.method)),
    [fund.destinations],
  )
  const displayOnly = useMemo(
    () => fund.destinations.filter((d) => !LINKABLE.includes(d.method) || !d.confirmed),
    [fund.destinations],
  )

  const [amountC, setAmountC] = useState(fund.suggestedC || 1500)
  const [method, setMethod] = useState<PayMethod>(available[0]?.method ?? 'venmo')
  const [links, setLinks] = useState<Record<string, LinkState>>({})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setAmountC(fund.suggestedC || 1500)
    setMethod(available[0]?.method ?? 'venmo')
    setError(null)
    setCopied(null)
  }, [open, fund.suggestedC, available])

  // Links are regenerated whenever the amount changes — a cached link with a
  // stale amount is worse than no link at all (D43).
  useEffect(() => {
    if (!open || !available.length) return
    const ac = new AbortController()
    const t = setTimeout(() => {
      for (const dest of available) {
        GET<PayLink>(`/api/fund/${weekId}/link?method=${dest.method}&amountC=${amountC}`, ac.signal)
          .then((link) => setLinks((prev) => ({ ...prev, [dest.method]: { link, error: null } })))
          .catch((e: unknown) => {
            if (ac.signal.aborted) return
            setLinks((prev) => ({
              ...prev,
              [dest.method]: {
                link: null,
                error: e instanceof ApiFailure ? e.message : 'That link isn’t available right now.',
              },
            }))
          })
      }
    }, 220)
    return () => { ac.abort(); clearTimeout(t) }
  }, [open, weekId, amountC, available])

  const active = links[method] ?? { link: null, error: null }
  const activeDest = available.find((d) => d.method === method) ?? null
  const handle = active.link?.handle ?? activeDest?.handle ?? fund.destinations[0]?.handle ?? null
  const amountText = active.link?.amountDisplay ?? `$${(amountC / 100).toFixed(2)}`
  const qrPayload = active.link?.qrPayload ?? null

  const copy = useCallback(async (value: string, tag: string) => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(tag)
      setTimeout(() => setCopied((c) => (c === tag ? null : c)), 2000)
    } catch {
      setError('Couldn’t copy. Select the text instead.')
    }
  }, [])

  const claim = useCallback(async () => {
    setBusy(true)
    setError(null)
    try {
      const res = await writeOrQueue<{ contribution: Contribution }>(
        `/api/fund/${weekId}/contributions`, 'POST',
        { amountC, method },
        `Chipped in ${money(amountC)}`,
      )
      await onDone(
        res === null
          ? 'Saved. It will post when you’re back online'
          // Chip-ins confirm on arrival now, so the total on Home has already
          // moved by the time this message is read.
          : `Recorded. ${money(amountC)} is in the fund.`,
      )
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : 'Couldn’t record that.')
    } finally {
      setBusy(false)
    }
  }, [amountC, method, onDone, weekId])

  const qr = qrPayload ? (
    <QrBlock payload={qrPayload} size={desktop ? 208 : 84} />
  ) : (
    <QrPlaceholder size={desktop ? 208 : 84} />
  )

  const copyLines = (
    <div className="mono" style={{ fontSize: 12, lineHeight: 2, minWidth: 0 }}>
      <div>
        {handle ?? '-'}{' '}
        {handle ? (
          /* Negative margin keeps the 44px hit area from stretching the line. */
          <button
            onClick={() => void copy(handle, 'handle')}
            aria-label={`Copy the handle ${handle}`}
            style={{ color: 'var(--bright)', fontSize: 12, padding: '14px 8px', margin: '-14px -4px' }}
          >
            {copied === 'handle' ? 'copied' : 'copy'}
          </button>
        ) : null}
      </div>
      <div>
        {amountText}{' '}
        <button
          onClick={() => void copy(amountText.replace('$', ''), 'amount')}
          aria-label={`Copy the amount ${amountText}`}
          style={{ color: 'var(--bright)', fontSize: 12, padding: '14px 8px', margin: '-14px -4px' }}
        >
          {copied === 'amount' ? 'copied' : 'copy'}
        </button>
      </div>
    </div>
  )

  const deepLinkButtons = available.map((dest, i) => {
    const st = links[dest.method]
    const href = st?.link?.deepLink ?? null
    return (
      <a
        key={dest.method}
        href={href ?? undefined}
        target="_blank"
        rel="noreferrer"
        onClick={() => setMethod(dest.method)}
        aria-disabled={href ? undefined : true}
        style={{
          display: 'block', width: '100%', textAlign: 'center',
          padding: 14, borderRadius: 'var(--radius-btn)', minHeight: 48,
          fontSize: 15, fontWeight: i === 0 ? 700 : 600,
          background: i === 0 ? 'var(--fill)' : 'transparent',
          color: i === 0 ? 'var(--ink)' : 'var(--fg)',
          border: i === 0 ? 'none' : '1px solid var(--line)',
          opacity: href ? 1 : 0.5,
          pointerEvents: href ? 'auto' : 'none',
        }}
      >
        Pay with {METHOD_LABEL[dest.method]}
      </a>
    )
  })

  return (
    <Sheet open={open} onClose={onClose} labelledBy="sfg-chip-in">
      <div id="sfg-chip-in" style={{ fontSize: 19, fontWeight: 800 }}>
        Chip in for {fund.weekLabel}
      </div>
      <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 4 }}>
        Goes straight into this week’s food
      </div>

      <BigStepper
        value={amountC}
        onChange={setAmountC}
        min={500}
        max={20_000}
        step={500}
        format={money}
      />

      <div style={{ fontSize: 12, color: 'var(--dim)', marginTop: 14, lineHeight: 1.5 }}>
        Send as a personal payment. Don’t toggle “Turn on for purchases.”
      </div>

      {available.length > 1 ? (
        <div style={{ display: 'flex', gap: 6, marginTop: 14, flexWrap: 'wrap' }}>
          {available.map((d) => {
            const on = d.method === method
            return (
              <button
                key={d.method}
                onClick={() => setMethod(d.method)}
                aria-pressed={on}
                className="mono"
                style={{
                  fontSize: 11, fontWeight: 600, padding: '8px 14px', borderRadius: 999, minHeight: 44,
                  background: on ? 'var(--fill)' : 'transparent',
                  color: on ? 'var(--ink)' : 'var(--dim)',
                  border: on ? '1px solid var(--fill)' : '1px solid var(--line)',
                }}
              >
                {METHOD_LABEL[d.method]}
              </button>
            )
          })}
        </div>
      ) : null}

      {desktop ? (
        /* D44 — the QR leads, the deep links demote to a text link. */
        <>
          <div style={{ display: 'flex', gap: 20, alignItems: 'center', marginTop: 18 }}>
            <div style={{ flex: '0 0 auto' }}>{qr}</div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700 }}>Scan with your phone</div>
              <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 4, lineHeight: 1.5 }}>
                The amount and note are already in the code.
              </div>
              <Spacer h={10} />
              {copyLines}
            </div>
          </div>
          {available.length ? (
            <div style={{ marginTop: 14 }}>
              {available.map((dest) => {
                const href = links[dest.method]?.link?.deepLink
                if (!href) return null
                return (
                  <a
                    key={dest.method}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => setMethod(dest.method)}
                    style={{ display: 'inline-block', fontSize: 13, fontWeight: 600, marginRight: 16, minHeight: 32 }}
                  >
                    Open {METHOD_LABEL[dest.method]} in browser ↗
                  </a>
                )
              })}
            </div>
          ) : null}
        </>
      ) : (
        <>
          {available.length ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
              {deepLinkButtons}
            </div>
          ) : null}

          {/* Always present, never conditional on the link working (D42). */}
          <div style={{ marginTop: 18, display: 'flex', gap: 14, alignItems: 'center' }}>
            <div style={{ flex: '0 0 auto' }}>{qr}</div>
            {copyLines}
          </div>
        </>
      )}

      {active.error ? <><Spacer h={12} /><Notice tone="warn">{active.error}</Notice></> : null}
      {!available.length ? (
        <>
          <Spacer h={12} />
          <Notice tone="info">
            Nobody has set up a payment link for this week yet. Hand over cash and record it
            below.
          </Notice>
        </>
      ) : null}

      {displayOnly.length ? (
        <div className="mono" style={{ fontSize: 11, color: 'var(--dim)', marginTop: 12, lineHeight: 1.8 }}>
          {displayOnly.map((d) => (
            <div key={d.method}>{METHOD_LABEL[d.method]}: {d.handle}</div>
          ))}
        </div>
      ) : null}

      {error ? <><Spacer h={12} /><Notice tone="warn">{error}</Notice></> : null}

      <button
        onClick={() => void claim()}
        disabled={busy}
        style={{
          display: 'block', width: '100%', marginTop: 18, paddingTop: 16,
          borderTop: '1px solid var(--line)', textAlign: 'center',
          fontSize: 14, fontWeight: 700, color: 'var(--bright)', minHeight: 48,
          opacity: busy ? 0.6 : 1,
        }}
      >
        {busy ? 'Recording…' : `✓ I sent ${money(amountC)}`}
      </button>
    </Sheet>
  )
}

// ── fixing one you already made ─────────────────────────────────────────────

/**
 * Deliberately not the contribute sheet in an "edit" mode.
 *
 * That sheet is about *sending* money: QR code, deep links, a handle to copy,
 * the reminder about personal payments. None of it applies here — the money
 * has already moved, or it hasn't and that is between two people. What is left
 * is the two facts the app actually stores, and a way out for someone who
 * recorded a payment they never made.
 *
 * "Remove" voids rather than deletes: `DELETE /api/fund/contributions/:id`
 * keeps the row and marks it voided (D45), so the ledger still shows that
 * something was claimed and withdrawn rather than quietly losing it.
 */
function EditContributionSheet({
  contribution, onClose, onDone,
}: {
  contribution: Contribution
  onClose: () => void
  onDone: (message?: string) => Promise<void>
}) {
  const [amountC, setAmountC] = useState(contribution.amountC)
  const [method, setMethod] = useState<PayMethod>(contribution.method)
  const [busy, setBusy] = useState(false)
  const [confirmRemove, setConfirmRemove] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const changed = amountC !== contribution.amountC || method !== contribution.method

  const save = useCallback(async () => {
    setBusy(true)
    setError(null)
    try {
      await PATCH<{ contribution: Contribution }>(
        `/api/fund/contributions/${encodeURIComponent(contribution.id)}`,
        { amountC, method },
      )
      await onDone(`Updated to ${money(amountC)}.`)
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : 'Couldn’t save that.')
    } finally {
      setBusy(false)
    }
  }, [amountC, contribution.id, method, onDone])

  const remove = useCallback(async () => {
    setBusy(true)
    setError(null)
    try {
      await DEL(`/api/fund/contributions/${encodeURIComponent(contribution.id)}`)
      await onDone('Taken back out of the fund.')
    } catch (e) {
      setError(e instanceof ApiFailure ? e.message : 'Couldn’t remove that.')
    } finally {
      setBusy(false)
    }
  }, [contribution.id, onDone])

  return (
    <Sheet open onClose={onClose} labelledBy="sfg-edit-chip-in">
      <div id="sfg-edit-chip-in" style={{ fontSize: 19, fontWeight: 800 }}>
        Edit your chip-in
      </div>
      <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 4 }}>
        Recorded {money(contribution.amountC)} by {METHOD_LABEL[contribution.method]}. Changing
        it here changes the fund total.
      </div>

      <BigStepper
        value={amountC}
        onChange={setAmountC}
        min={500}
        max={20_000}
        step={500}
        format={money}
      />

      <Eyebrow style={{ marginTop: 18 }}>Sent by</Eyebrow>
      <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
        {(Object.keys(METHOD_LABEL) as PayMethod[]).map((m) => {
          const on = m === method
          return (
            <button
              key={m}
              onClick={() => setMethod(m)}
              aria-pressed={on}
              className="mono"
              style={{
                fontSize: 11, fontWeight: 600, padding: '8px 14px', borderRadius: 999, minHeight: 44,
                background: on ? 'var(--fill)' : 'transparent',
                color: on ? 'var(--ink)' : 'var(--dim)',
                border: on ? '1px solid var(--fill)' : '1px solid var(--outline)',
              }}
            >
              {METHOD_LABEL[m]}
            </button>
          )
        })}
      </div>

      {contribution.status === 'disputed' ? (
        <>
          <Spacer h={14} />
          <Notice tone="info">
            The host said they didn’t see this one. Correcting the amount doesn’t clear that —
            they’ll need to mark it received.
          </Notice>
        </>
      ) : null}

      {error ? <><Spacer h={12} /><Notice tone="warn">{error}</Notice></> : null}

      <Spacer h={18} />
      <Button disabled={busy || !changed} onClick={() => void save()}>
        {busy ? 'Saving…' : changed ? `Save ${money(amountC)}` : 'Nothing changed yet'}
      </Button>

      {confirmRemove ? (
        <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--line)' }}>
          <div style={{ fontSize: 13, lineHeight: 1.5 }}>
            Take {money(contribution.amountC)} back out of the fund? The total drops by that much.
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button
              onClick={() => void remove()}
              disabled={busy}
              style={{
                flex: 1, padding: 12, borderRadius: 10, minHeight: 44, fontSize: 13, fontWeight: 700,
                border: '1px solid var(--outline)', color: 'var(--fg)', opacity: busy ? 0.6 : 1,
              }}
            >
              {busy ? '…' : 'Remove it'}
            </button>
            <button
              onClick={() => setConfirmRemove(false)}
              disabled={busy}
              style={{
                flex: 1, padding: 12, borderRadius: 10, minHeight: 44, fontSize: 13, fontWeight: 700,
                background: 'var(--fill)', color: 'var(--ink)', opacity: busy ? 0.6 : 1,
              }}
            >
              Keep it
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setConfirmRemove(true)}
          style={{
            display: 'block', width: '100%', marginTop: 16, paddingTop: 14,
            borderTop: '1px solid var(--line)', textAlign: 'center',
            fontSize: 13, fontWeight: 600, color: 'var(--dim)', minHeight: 48,
          }}
        >
          I didn’t actually send this
        </button>
      )}
    </Sheet>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// QR code — byte mode, error correction M, generated locally (D43).
//
// A compact implementation of ISO/IEC 18004: pick the smallest version that
// fits, Reed–Solomon the data, interleave the blocks, lay them out in the
// zigzag, then pick the mask with the lowest penalty. No dependencies and no
// network call, because the payload contains the host's handle and the amount.
// ════════════════════════════════════════════════════════════════════════════

/** Error-correction codewords per block, level M, indexed by version. */
const ECC_PER_BLOCK_M = [
  -1, 10, 16, 26, 18, 24, 16, 18, 22, 22, 26, 30, 22, 22, 24, 24, 28, 28, 26, 26, 26,
  26, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28, 28,
]

/** Number of error-correction blocks, level M, indexed by version. */
const ECC_BLOCKS_M = [
  -1, 1, 1, 1, 2, 2, 4, 4, 4, 5, 5, 5, 8, 9, 9, 10, 10, 11, 13, 14, 16, 17, 17, 18,
  20, 21, 23, 25, 26, 28, 29, 31, 33, 35, 37, 38, 40, 43, 45, 47, 49,
]

const PENALTY_N1 = 3
const PENALTY_N2 = 3
const PENALTY_N3 = 40
const PENALTY_N4 = 10

function numRawDataModules(version: number): number {
  let result = (16 * version + 128) * version + 64
  if (version >= 2) {
    const numAlign = Math.floor(version / 7) + 2
    result -= (25 * numAlign - 10) * numAlign - 55
    if (version >= 7) result -= 36
  }
  return result
}

function numDataCodewords(version: number): number {
  return Math.floor(numRawDataModules(version) / 8)
    - ECC_PER_BLOCK_M[version] * ECC_BLOCKS_M[version]
}

function gfMultiply(x: number, y: number): number {
  let z = 0
  for (let i = 7; i >= 0; i--) {
    z = (z << 1) ^ ((z >>> 7) * 0x11d)
    z ^= ((y >>> i) & 1) * x
  }
  return z & 0xff
}

function rsDivisor(degree: number): number[] {
  const result: number[] = new Array<number>(degree).fill(0)
  result[degree - 1] = 1
  let root = 1
  for (let i = 0; i < degree; i++) {
    for (let j = 0; j < degree; j++) {
      result[j] = gfMultiply(result[j], root)
      if (j + 1 < degree) result[j] ^= result[j + 1]
    }
    root = gfMultiply(root, 0x02)
  }
  return result
}

function rsRemainder(data: number[], divisor: number[]): number[] {
  const result: number[] = new Array<number>(divisor.length).fill(0)
  for (const b of data) {
    const factor = b ^ (result.shift() as number)
    result.push(0)
    for (let i = 0; i < divisor.length; i++) result[i] ^= gfMultiply(divisor[i], factor)
  }
  return result
}

function alignmentPositions(version: number): number[] {
  if (version === 1) return []
  const numAlign = Math.floor(version / 7) + 2
  const step = version === 32 ? 26 : Math.ceil((version * 4 + 4) / (numAlign * 2 - 2)) * 2
  const result: number[] = [6]
  for (let pos = version * 4 + 10; result.length < numAlign; pos -= step) result.splice(1, 0, pos)
  return result
}

function getBit(x: number, i: number): boolean {
  return ((x >>> i) & 1) !== 0
}

interface QrCode { size: number; modules: boolean[][] }

/**
 * Encode `text` as a QR code. Returns null only if the text is too long for
 * version 40 at level M (2,331 bytes) — no payment URL comes close.
 */
export function qrEncode(text: string): QrCode | null {
  const bytes = Array.from(new TextEncoder().encode(text))

  let version = 1
  for (; version <= 40; version++) {
    const capacityBits = numDataCodewords(version) * 8
    const countBits = version <= 9 ? 8 : 16
    if (4 + countBits + bytes.length * 8 <= capacityBits) break
  }
  if (version > 40) return null

  // ── bit stream ────────────────────────────────────────────────────────────
  const bits: number[] = []
  const appendBits = (value: number, length: number): void => {
    for (let i = length - 1; i >= 0; i--) bits.push((value >>> i) & 1)
  }
  appendBits(0b0100, 4)                                  // byte mode
  appendBits(bytes.length, version <= 9 ? 8 : 16)
  for (const b of bytes) appendBits(b, 8)

  const capacityBits = numDataCodewords(version) * 8
  appendBits(0, Math.min(4, capacityBits - bits.length)) // terminator
  appendBits(0, (8 - bits.length % 8) % 8)               // byte align
  for (let pad = 0xec; bits.length < capacityBits; pad ^= 0xec ^ 0x11) appendBits(pad, 8)

  const dataCodewords: number[] = new Array<number>(bits.length / 8).fill(0)
  bits.forEach((bit, i) => { dataCodewords[i >>> 3] |= bit << (7 - (i & 7)) })

  // ── blocks + Reed–Solomon ─────────────────────────────────────────────────
  const numBlocks = ECC_BLOCKS_M[version]
  const blockEccLen = ECC_PER_BLOCK_M[version]
  const rawCodewords = Math.floor(numRawDataModules(version) / 8)
  const numShortBlocks = numBlocks - rawCodewords % numBlocks
  const shortBlockLen = Math.floor(rawCodewords / numBlocks)
  const shortDataLen = shortBlockLen - blockEccLen

  const divisor = rsDivisor(blockEccLen)
  const blocks: number[][] = []
  for (let i = 0, k = 0; i < numBlocks; i++) {
    const len = shortDataLen + (i < numShortBlocks ? 0 : 1)
    const dat = dataCodewords.slice(k, k + len)
    k += len
    const ecc = rsRemainder(dat, divisor)
    // Short blocks carry a placeholder so the interleave stays column-aligned.
    const padded = i < numShortBlocks ? [...dat, 0] : [...dat]
    blocks.push([...padded, ...ecc])
  }

  const interleaved: number[] = []
  for (let i = 0; i < blocks[0].length; i++) {
    for (let j = 0; j < blocks.length; j++) {
      if (i !== shortDataLen || j >= numShortBlocks) interleaved.push(blocks[j][i])
    }
  }

  // ── the grid ──────────────────────────────────────────────────────────────
  const size = version * 4 + 17
  const modules: boolean[][] = Array.from({ length: size }, () => new Array<boolean>(size).fill(false))
  const isFunction: boolean[][] = Array.from({ length: size }, () => new Array<boolean>(size).fill(false))

  const setFunctionModule = (x: number, y: number, dark: boolean): void => {
    if (x < 0 || y < 0 || x >= size || y >= size) return
    modules[y][x] = dark
    isFunction[y][x] = true
  }

  const drawFinder = (x: number, y: number): void => {
    for (let dy = -4; dy <= 4; dy++) {
      for (let dx = -4; dx <= 4; dx++) {
        const dist = Math.max(Math.abs(dx), Math.abs(dy))
        setFunctionModule(x + dx, y + dy, dist !== 2 && dist !== 4)
      }
    }
  }

  const drawAlignment = (x: number, y: number): void => {
    for (let dy = -2; dy <= 2; dy++) {
      for (let dx = -2; dx <= 2; dx++) {
        setFunctionModule(x + dx, y + dy, Math.max(Math.abs(dx), Math.abs(dy)) !== 1)
      }
    }
  }

  const drawFormatBits = (mask: number): void => {
    // Level M's format value is 0b00.
    const data = (0b00 << 3) | mask
    let rem = data
    for (let i = 0; i < 10; i++) rem = (rem << 1) ^ ((rem >>> 9) * 0x537)
    const value = ((data << 10) | rem) ^ 0x5412

    for (let i = 0; i <= 5; i++) setFunctionModule(8, i, getBit(value, i))
    setFunctionModule(8, 7, getBit(value, 6))
    setFunctionModule(8, 8, getBit(value, 7))
    setFunctionModule(7, 8, getBit(value, 8))
    for (let i = 9; i < 15; i++) setFunctionModule(14 - i, 8, getBit(value, i))

    for (let i = 0; i < 8; i++) setFunctionModule(size - 1 - i, 8, getBit(value, i))
    for (let i = 8; i < 15; i++) setFunctionModule(8, size - 15 + i, getBit(value, i))
    setFunctionModule(8, size - 8, true) // the always-dark module
  }

  // timing patterns
  for (let i = 0; i < size; i++) {
    setFunctionModule(6, i, i % 2 === 0)
    setFunctionModule(i, 6, i % 2 === 0)
  }
  drawFinder(3, 3)
  drawFinder(size - 4, 3)
  drawFinder(3, size - 4)

  const alignPos = alignmentPositions(version)
  for (let i = 0; i < alignPos.length; i++) {
    for (let j = 0; j < alignPos.length; j++) {
      // The three corners already hold finder patterns.
      const corner = (i === 0 && j === 0)
        || (i === 0 && j === alignPos.length - 1)
        || (i === alignPos.length - 1 && j === 0)
      if (!corner) drawAlignment(alignPos[i], alignPos[j])
    }
  }

  drawFormatBits(0) // reserved; overwritten once the mask is chosen

  if (version >= 7) {
    let rem = version
    for (let i = 0; i < 12; i++) rem = (rem << 1) ^ ((rem >>> 11) * 0x1f25)
    const value = (version << 12) | rem
    for (let i = 0; i < 18; i++) {
      const dark = getBit(value, i)
      const a = size - 11 + i % 3
      const b = Math.floor(i / 3)
      setFunctionModule(a, b, dark)
      setFunctionModule(b, a, dark)
    }
  }

  // ── data placement ────────────────────────────────────────────────────────
  let i = 0
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5
    for (let vert = 0; vert < size; vert++) {
      for (let j = 0; j < 2; j++) {
        const x = right - j
        const upward = ((right + 1) & 2) === 0
        const y = upward ? size - 1 - vert : vert
        if (!isFunction[y][x] && i < interleaved.length * 8) {
          modules[y][x] = getBit(interleaved[i >>> 3], 7 - (i & 7))
          i++
        }
      }
    }
  }

  // ── masking ───────────────────────────────────────────────────────────────
  const maskInvert = (mask: number, x: number, y: number): boolean => {
    switch (mask) {
      case 0: return (x + y) % 2 === 0
      case 1: return y % 2 === 0
      case 2: return x % 3 === 0
      case 3: return (x + y) % 3 === 0
      case 4: return (Math.floor(x / 3) + Math.floor(y / 2)) % 2 === 0
      case 5: return (x * y) % 2 + (x * y) % 3 === 0
      case 6: return ((x * y) % 2 + (x * y) % 3) % 2 === 0
      default: return ((x + y) % 2 + (x * y) % 3) % 2 === 0
    }
  }

  const applyMask = (mask: number): void => {
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        if (!isFunction[y][x] && maskInvert(mask, x, y)) modules[y][x] = !modules[y][x]
      }
    }
  }

  const addHistory = (runLength: number, history: number[]): void => {
    if (history[0] === 0) runLength += size // the light border counts
    history.pop()
    history.unshift(runLength)
  }

  const countFinderPatterns = (history: number[]): number => {
    const n = history[1]
    const core = n > 0 && history[2] === n && history[3] === n * 3 && history[4] === n && history[5] === n
    return (core && history[0] >= n * 4 && history[6] >= n ? 1 : 0)
      + (core && history[6] >= n * 4 && history[0] >= n ? 1 : 0)
  }

  const terminateAndCount = (runColor: boolean, runLength: number, history: number[]): number => {
    let length = runLength
    if (runColor) { addHistory(length, history); length = 0 }
    length += size
    addHistory(length, history)
    return countFinderPatterns(history)
  }

  const penalty = (): number => {
    let result = 0
    for (let y = 0; y < size; y++) {
      let runColor = false
      let run = 0
      const history = [0, 0, 0, 0, 0, 0, 0]
      for (let x = 0; x < size; x++) {
        if (modules[y][x] === runColor) {
          run++
          if (run === 5) result += PENALTY_N1
          else if (run > 5) result++
        } else {
          addHistory(run, history)
          if (!runColor) result += countFinderPatterns(history) * PENALTY_N3
          runColor = modules[y][x]
          run = 1
        }
      }
      result += terminateAndCount(runColor, run, history) * PENALTY_N3
    }
    for (let x = 0; x < size; x++) {
      let runColor = false
      let run = 0
      const history = [0, 0, 0, 0, 0, 0, 0]
      for (let y = 0; y < size; y++) {
        if (modules[y][x] === runColor) {
          run++
          if (run === 5) result += PENALTY_N1
          else if (run > 5) result++
        } else {
          addHistory(run, history)
          if (!runColor) result += countFinderPatterns(history) * PENALTY_N3
          runColor = modules[y][x]
          run = 1
        }
      }
      result += terminateAndCount(runColor, run, history) * PENALTY_N3
    }
    for (let y = 0; y < size - 1; y++) {
      for (let x = 0; x < size - 1; x++) {
        const c = modules[y][x]
        if (c === modules[y][x + 1] && c === modules[y + 1][x] && c === modules[y + 1][x + 1]) {
          result += PENALTY_N2
        }
      }
    }
    let dark = 0
    for (const row of modules) for (const cell of row) if (cell) dark++
    const total = size * size
    const k = Math.ceil(Math.abs(dark * 20 - total * 10) / total) - 1
    return result + k * PENALTY_N4
  }

  let bestMask = 0
  let bestPenalty = Infinity
  for (let mask = 0; mask < 8; mask++) {
    applyMask(mask)
    drawFormatBits(mask)
    const p = penalty()
    if (p < bestPenalty) { bestPenalty = p; bestMask = mask }
    applyMask(mask) // masking is its own inverse
  }
  applyMask(bestMask)
  drawFormatBits(bestMask)

  return { size, modules }
}

/**
 * The QR itself. Black on white with a four-module quiet zone — those two
 * colours are a scanning requirement, not a theme choice, so they stay outside
 * the team palette.
 */
function QrBlock({ payload, size }: { payload: string; size: number }) {
  const code = useMemo(() => {
    try { return qrEncode(payload) } catch { return null }
  }, [payload])

  if (!code) return <QrPlaceholder size={size} />

  const quiet = 4
  const dim = code.size + quiet * 2
  let path = ''
  for (let y = 0; y < code.size; y++) {
    for (let x = 0; x < code.size; x++) {
      if (code.modules[y][x]) path += `M${x + quiet} ${y + quiet}h1v1h-1z`
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center' }}>
      <svg
        viewBox={`0 0 ${dim} ${dim}`}
        width={size}
        height={size}
        role="img"
        aria-label="Payment QR code: scan to pay the host this amount"
        style={{ borderRadius: 10, display: 'block' }}
        shapeRendering="crispEdges"
      >
        <rect width={dim} height={dim} fill="white" />
        <path d={path} fill="black" />
      </svg>
    </div>
  )
}

/** No link format exists for this method, so there is nothing to encode. */
function QrPlaceholder({ size }: { size: number }) {
  return (
    <div
      aria-hidden="true"
      style={{
        width: size, height: size, borderRadius: 10, flex: '0 0 auto',
        border: '1px dashed var(--line)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 11, color: 'var(--dim)', textAlign: 'center', padding: 8,
      }}
    >
      no link to encode
    </div>
  )
}
